import * as React from "react";
import { Button } from "./ui/button";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import useSizes from "@/lib/window-sizes-hook";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./ui/drawer";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { useTranslations } from "next-intl";

interface FlexiblePopoverProps {
  header?: string;
  placeholder: string;
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customIcon?: React.ReactNode;
  className?: string;
  fullWidth?: boolean;
  fitContent?: boolean;
  isNeedDrawer?: boolean;
}
const FlexiblePopover = ({
  header,
  children,
  onOpenChange,
  open,
  placeholder,
  customIcon,
  className,
  fitContent,
  isNeedDrawer,
}: FlexiblePopoverProps) => {
  const ref = React.useRef<HTMLButtonElement>(null);
  const isClient = useSelector((state: RootState) => state.isBrowser);
  const { maxSm } = useSizes();
  const t = useTranslations();

  if (isClient && (maxSm || isNeedDrawer)) {
    return (
      <Drawer
        dismissible
        direction="bottom"
        open={open}
        onOpenChange={onOpenChange}
      >
        <DrawerTrigger asChild>
          <Button
            title={header}
            variant={"ghost"}
            aria-label={header}
            type="button"
            className={cn(
              "w-full relative justify-between border !h-14 appearance-none bg-transparent text-gray-dark",
              {
                "p-3": !header,
                "px-3 pt-4 pb-0": header,
              }
            )}
          >
            {header && (
              <span className="text-muted text-[12px] font-light absolute ltr:left-3 rtl:right-3 -translate-y-5">
                {placeholder}
              </span>
            )}
            <span className="max-w-56 truncate">{header || placeholder}</span>
            {customIcon ? (
              customIcon
            ) : (
              <Play className="ms-2 rotate-90 fill-black text-black size-4 shrink-0 opacity-50" />
            )}
          </Button>
        </DrawerTrigger>
        <DrawerContent className={cn("flex flex-col inset-y-auto bottom-0 left-auto right-auto",
          {
            "w-full": isNeedDrawer,
          }
        )}>
          <DrawerHeader>
            <DrawerTitle>{header || placeholder}</DrawerTitle>
          </DrawerHeader>
          {children}
          <DrawerClose asChild>
            <Button className="w-full uppercase">{t("done")}</Button>
          </DrawerClose>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover {...{ onOpenChange, open }}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          title={header}
          variant={"ghost"}
          type="button"
          aria-label={header}
          className={cn(
            "w-full relative justify-between border !h-14 appearance-none bg-transparent text-gray-dark",
            {
              "p-3": !header,
              "px-3 pt-4 pb-0": header,
            }
          )}
          aria-haspopup="listbox"
        >
          {header && (
            <span className="text-muted text-[12px] font-light absolute ltr:left-3 rtl:right-3 -translate-y-5">
              {placeholder}
            </span>
          )}
          <span className="max-w-56 truncate">{header || placeholder}</span>
          {customIcon ? (
            customIcon
          ) : (
            <Play className="ms-2 rotate-90 fill-black text-black size-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-0 w-full", { "!w-fit": fitContent })}>
        <div
          className={cn("w-full", className)}
          style={
            !fitContent
              ? {
                  minWidth: `${ref.current?.offsetWidth}px`,
                }
              : undefined
          }
        >
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default FlexiblePopover;
